import React from "react";

// Wraps every occurrence of the search words in `text` with a <mark>, so the user can
// see WHY a row matched. With a fuzzy, multi-word, server-side search that is not
// obvious — "clas" matching "5mtr Measuring Tape CLASSIC" reads as a random hit until
// the matched letters are picked out.
//
// Lived as an identical copy in both POS search boxes; hoisted here when the stock
// transfer picker became the third caller, which is this project's threshold for
// extracting a shared helper.
//
// Every keyword is regex-escaped before it goes near a RegExp: the search box accepts
// anything a user or a barcode scanner can type, and a stray "(" or "*" would
// otherwise throw while they were mid-word.
//
// { wordStart: true } marks a keyword only where it begins a word — the rule the
// product search matches by (SearchTerms on the server). Without it "kita" would light
// up inside "Makita" on a row that matched for some other reason, and the highlight
// would explain a match that never happened. A piece holding a digit is a code fragment
// and is marked wherever it sits ("81011" inside "RD81011") — the server matches those
// anywhere in a name (SearchTerms.NamePattern).
const WORD_CHAR = /[\p{L}\p{N}]/u;
const DIGIT = /\p{N}/u;

const highlightMatch = (text, query, { wordStart = false } = {}) => {
  if (!text || !query) return text;

  const keywords = query.trim().split(/\s+/).filter(Boolean);
  if (keywords.length === 0) return text;

  const escaped = keywords.map((kw) => kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const regex = new RegExp(`(${escaped.join("|")})`, "gi");

  // String.split with a capturing group keeps the delimiters, so the array alternates
  // unmatched / matched / unmatched — odd indexes are the matches. The running offset
  // is what lets wordStart look at the character before each one; a lookbehind would
  // do it in the regex, but older Safari cannot parse one and the page would crash.
  let offset = 0;
  return text.split(regex).map((part, i) => {
    const at = offset;
    offset += part.length;
    const isMatch =
      i % 2 === 1 &&
      (!wordStart || at === 0 || !WORD_CHAR.test(text[at - 1]) || DIGIT.test(part));
    return isMatch ? (
      <mark
        key={i}
        style={{
          backgroundColor: "var(--primary-color)",
          color: "#fff",
          borderRadius: "3px",
          padding: "0 3px",
          fontWeight: 600,
          fontSize: "inherit",
        }}
      >
        {part}
      </mark>
    ) : (
      part
    );
  });
};

// Exported both ways on purpose: ServiceContract/Form/SelectService.jsx has always
// imported the named form, and rewriting a working call site to suit a new one is how
// unrelated screens get broken.
export { highlightMatch };
export default highlightMatch;
